import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, CalendarCheck, FlaskConical, Scan } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { trackAgendarCitaClick } from "@/analytics";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const WaveCanvas = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let t = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        for (let x = 0; x <= w; x += 5 * dpr) {
          const amp = (22 + i * 16) * dpr * (0.6 + 0.4 * Math.sin(t * 0.35 + i));
          const y =
            h * 0.55 +
            Math.sin(x * 0.0035 + t * 0.9 + i * 1.3) * amp +
            Math.sin(x * 0.001 + t * 0.4 + i) * amp * 0.4;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle =
          i % 2 === 0
            ? `rgba(220, 38, 38, ${0.12 - i * 0.015})`
            : `rgba(20, 20, 20, ${0.1 - i * 0.012})`;
        ctx.lineWidth = 1.1 * dpr;
        ctx.stroke();
      }
      t += 0.012;
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  );
};

const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="noise-overlay relative flex min-h-screen items-center overflow-hidden pt-24"
    >
      <WaveCanvas />
      <div className="pointer-events-none absolute -top-32 right-[-10%] h-[520px] w-[520px] rounded-full bg-red-200/40 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[460px] w-[460px] rounded-full bg-red-200/40 blur-[120px]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-6 py-16 lg:grid-cols-12"
      >
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
            data-testid="hero-overline"
            className="mb-6 inline-flex items-center gap-3 rounded-full border border-red-600/20 bg-red-50 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.28em] text-red-700"
          >
            <span className="pulse-ring inline-block h-2 w-2 rounded-full bg-red-600" />
            Diagnóstico por imágenes · Laboratorio clínico · Atención integral
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl font-light leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.25}>Diagnóstico de</MaskedLine>
            <MaskedLine delay={0.38}>
              <span className="font-semibold">alta precisión</span>,
            </MaskedLine>
            <MaskedLine delay={0.51}>
              cuidado{" "}
              <em className="font-light italic text-red-600">humano</em>.
            </MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            data-testid="hero-subtext"
            className="mt-7 max-w-xl"
          >
            <p className="text-base font-semibold leading-relaxed text-slate-900">
              Nuestra Atención Médica: tu salud en manos expertas y humanas
            </p>
            <p className="mt-2 text-base leading-relaxed text-slate-600">
              En ECOCORE DX entendemos que cada paciente es único. Nos alejamos
              de las consultas apresuradas para ofrecerte un diagnóstico preciso
              y un trato cálido, en un ambiente diseñado para tu comodidad y
              tranquilidad.
            </p>
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
              ¿Por qué elegir nuestro centro?
            </p>
            <ul className="mt-3 space-y-2.5">
              {[
                {
                  t: "Atención personalizada",
                  d: "Escuchamos tu historia clínica sin prisas para entender a fondo tus necesidades.",
                },
                {
                  t: "Instalaciones modernas",
                  d: "Equipos de última generación para realizar tus exámenes y diagnósticos en el mismo lugar.",
                },
              ].map((item) => (
                <li
                  key={item.t}
                  className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-600"
                >
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-600" />
                  <span>
                    <span className="font-semibold text-slate-800">
                      {item.t}:
                    </span>{" "}
                    {item.d}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-agendar-cta"
              onClick={() => {
                trackAgendarCitaClick("hero");
                scrollToId("contacto");
              }}
              className="group inline-flex items-center gap-2 rounded-full bg-black px-7 py-3.5 text-sm font-medium text-white transition-[background-color,transform] duration-300 hover:bg-red-700 hover:scale-[1.03] active:scale-95"
            >
              <CalendarCheck size={16} />
              Agendar una cita
            </button>
            <button
              data-testid="hero-servicios-cta"
              onClick={() => scrollToId("servicios")}
              className="group inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 text-sm font-medium text-slate-700 transition-[border-color,color,background-color] duration-300 hover:border-red-600 hover:text-red-700"
            >
              Ver servicios
              <ArrowDownRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5"
              />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            data-testid="hero-stats"
            className="mt-12 flex flex-wrap gap-3"
          >
            {[
              "11 servicios de atención",
              "Resultados el mismo día",
              "Atención personalizada",
            ].map((s) => (
              <span
                key={s}
                className="rounded-full border border-slate-200 bg-white/70 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-slate-500 backdrop-blur"
              >
                {s}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="relative hidden lg:col-span-5 lg:block">
          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
            className="relative"
          >
            <div className="overflow-hidden rounded-[2rem] border border-white shadow-2xl shadow-slate-900/10">
              <img
                data-testid="hero-image"
                src="/assets/dra-hero.jpg"
                alt="Dra. Lixandra García Peña en ECOCORE DX"
                className="aspect-[4/5] w-full object-cover object-top"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
              data-testid="hero-float-eco"
              className="absolute -left-10 top-10 flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/85 px-4 py-3 shadow-lg backdrop-blur-xl"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-red-700">
                <Scan size={16} />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Ecografía HD
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  Imagen en tiempo real
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.4, ease: EASE }}
              data-testid="hero-float-lab"
              className="absolute -bottom-6 -right-4 flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/85 px-4 py-3 shadow-lg backdrop-blur-xl"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-red-700">
                <FlaskConical size={16} />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Laboratorio clínico
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  Resultados el mismo día
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
